import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@wa/ui";
import { Pop } from "../shell/Pop";

export interface FilterMenuItem {
  value: string;
  label: string;
  /** How many rows this option would match. Shown quietly on the right. */
  count?: number;
  /** Utility class for a leading colour dot, when the option has a colour of
   *  its own (the score bands do). */
  dot?: string;
}

/**
 * A many-of-N filter that lives behind one pill.
 *
 * `MenuField` next to it is the one-of-N version: it picks a value and closes.
 * This one collects them, so it stays open while an operator ticks two
 * platforms and a condition, and the trigger carries the number of ticks -
 * otherwise a collapsed toolbar gives no clue that the list below is narrowed.
 *
 * It exists because a filter group written as a row of toggles costs one screen
 * row per group. Four groups is four rows of chips above the table, which is
 * how the site scanner ended up spending a third of the viewport on filters
 * before anyone had filtered anything.
 *
 * Must be rendered inside a `PopHost` - `Pop` reads its open slot from there,
 * and without one the trigger does nothing at all.
 */
export function FilterMenu({
  label,
  items,
  selected,
  onToggle,
  onClear,
  clearLabel,
  emptyLabel,
  className,
}: {
  label: string;
  items: FilterMenuItem[];
  selected: string[];
  onToggle: (value: string) => void;
  /** Offered as a row at the foot of the menu once anything is ticked. */
  onClear?: () => void;
  clearLabel?: string;
  /** Shown when the group has no options to offer yet. */
  emptyLabel?: string;
  className?: string;
}) {
  const count = selected.length;

  return (
    <Pop
      trigger={(open) => (
        <button
          type="button"
          // `crm-fld` is 280px wide by default, which is right for a search
          // field and wrong for four pills in a row. Utilities outrank the
          // component layer, so the width comes off here.
          className={cn(
            "crm-fld crm-fld-sel w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            (open || count > 0) && "is-on",
            className,
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span>{label}</span>
          {count > 0 ? <span className="crm-fld-n">{count}</span> : null}
          <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
        </button>
      )}
    >
      {items.length === 0 ? (
        <p className="crm-mut px-3.5 py-2.5 text-[12.5px]">{emptyLabel}</p>
      ) : (
        items.map((item) => {
          const on = selected.includes(item.value);
          return (
            <button
              key={item.value}
              type="button"
              role="menuitemcheckbox"
              aria-checked={on}
              // No close on click: ticking one option of a many-of-N filter is
              // rarely the whole thought.
              onClick={() => onToggle(item.value)}
              className={cn("crm-mi", on && "is-on")}
            >
              {item.dot ? <span className={cn("size-2 shrink-0 rounded-full", item.dot)} /> : null}
              <span className="crm-trunc flex-1">{item.label}</span>
              {item.count != null ? <span className="crm-mut crm-tnum text-[12px]">{item.count}</span> : null}
              {/* Kept in the layout when off, or every row shifts sideways as
                  the tick appears and disappears under the pointer. */}
              <Check className={cn("size-4", !on && "opacity-0")} />
            </button>
          );
        })
      )}

      {onClear && count > 0 ? (
        <>
          <div className="crm-menu-sep" />
          <button type="button" className="crm-mi" onClick={onClear}>
            <X className="size-4" />
            {clearLabel}
          </button>
        </>
      ) : null}
    </Pop>
  );
}
